import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import { Toaster } from "sonner";

// Whitelist of protected routes that are safe to round-trip through returnTo.
// Prevents open-redirect: only known internal paths are accepted.
const SAFE_RETURN_PATHS = new Set(["/dashboard", "/admin"]);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) {
    const path = location.pathname;
    const returnTo = SAFE_RETURN_PATHS.has(path) ? `&returnTo=${encodeURIComponent(path)}` : "";
    return <Navigate to={`/?signin=1${returnTo}`} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Legacy /login: safely redirect to homepage and auto-open the
              existing-user Sign In modal. Replaces history so users never see
              /login in their back-button. */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
